import type { EAnalyticMode, AnalyticInterval } from '~/modules/analytics/types';
import type { IAnalyticMetric, ICampaignAnalytic } from '~/types/metric';
import type { AnalyticDateFilter } from '~/presentation/ui';

import { useCustomCampaignAnalyticQueryV2 } from '../api/useCustomCampaignAnalyticQueryV2';
import { extractCampaignDetailData } from '../helpers/extractCampaignDetailData';
import { TIME_DIMENSION_TO_INTERVAL } from '../helpers/timeDimensionToInterval';
import { buildCampaignQueryVariables } from '../helpers/campaignAnalyticV2';
import { getTimeDimensionByDate } from '~/core/gemxql/helpers';
import { IVariantType } from '~/modules/campaign/types';
import { useMemo } from 'react';

export interface IFetchCampaignAnalyticDetailV2Props {
  campaignVariantIdA: string;
  campaignVariantIdB: string;
  /** Resolved from the campaign's own versions; scopes the current period only. */
  campaignVersionID?: string;
  currentDates: AnalyticDateFilter;
  previousDates: AnalyticDateFilter;
  isQueryPreviousData?: boolean;
  analyticMode: EAnalyticMode;
}

export interface IVariantIntervalSeries {
  data: IAnalyticMetric[];
  variantKey: IVariantType;
}

export interface IFetchCampaignAnalyticDetailV2Result {
  campaignAnalytic: ICampaignAnalytic[];
  previousCampaignAnalytic: ICampaignAnalytic[];
  metricInterval: IVariantIntervalSeries[];
  interval: AnalyticInterval;
  isLoadingMetric: boolean;
  isLoadingMetricInterval: boolean;
}

const buildAnalyticEntry = (
  campaignVariantID: string,
  variantKey: IVariantType,
  totalsMetric: IAnalyticMetric | undefined,
): ICampaignAnalytic => ({
  campaignGroupID: '',
  campaignVariantID,
  campaignVariantType: '',
  variantKey,
  metrics: totalsMetric ? [totalsMetric] : [],
});

export const useFetchCampaignAnalyticDetailV2 = ({
  campaignVariantIdA,
  campaignVariantIdB,
  campaignVersionID,
  currentDates,
  previousDates,
  isQueryPreviousData = true,
  analyticMode,
}: IFetchCampaignAnalyticDetailV2Props): IFetchCampaignAnalyticDetailV2Result => {
  const timeDimension = useMemo(
    () => getTimeDimensionByDate(currentDates.startDate, currentDates.endDate),
    [currentDates.startDate, currentDates.endDate],
  );

  // Only the current period is version-scoped; the compare window spans other versions by design.
  const currentVariablesA = useMemo(
    () =>
      buildCampaignQueryVariables({
        mode: analyticMode,
        campaignVariantID: campaignVariantIdA,
        campaignVersionID,
        dateRange: currentDates,
        interval: timeDimension,
      }),
    [analyticMode, campaignVariantIdA, campaignVersionID, currentDates, timeDimension],
  );

  const currentVariablesB = useMemo(
    () =>
      buildCampaignQueryVariables({
        mode: analyticMode,
        campaignVariantID: campaignVariantIdB,
        campaignVersionID,
        dateRange: currentDates,
        interval: timeDimension,
      }),
    [analyticMode, campaignVariantIdB, campaignVersionID, currentDates, timeDimension],
  );

  const previousVariablesA = useMemo(
    () =>
      buildCampaignQueryVariables({
        mode: analyticMode,
        campaignVariantID: campaignVariantIdA,
        dateRange: previousDates,
        interval: timeDimension,
      }),
    [analyticMode, campaignVariantIdA, previousDates, timeDimension],
  );

  const previousVariablesB = useMemo(
    () =>
      buildCampaignQueryVariables({
        mode: analyticMode,
        campaignVariantID: campaignVariantIdB,
        dateRange: previousDates,
        interval: timeDimension,
      }),
    [analyticMode, campaignVariantIdB, previousDates, timeDimension],
  );

  const isEnabledA = !!campaignVariantIdA && !!currentVariablesA.input;
  const isEnabledB = !!campaignVariantIdB && !!currentVariablesB.input;
  const isEnabledPrevA = isEnabledA && isQueryPreviousData && !!previousVariablesA.input;
  const isEnabledPrevB = isEnabledB && isQueryPreviousData && !!previousVariablesB.input;

  const { data: currentResponseA, isLoading: isLoadingCurrentA } = useCustomCampaignAnalyticQueryV2(
    currentVariablesA,
    {
      enabled: isEnabledA,
    },
  );
  const { data: currentResponseB, isLoading: isLoadingCurrentB } = useCustomCampaignAnalyticQueryV2(
    currentVariablesB,
    {
      enabled: isEnabledB,
    },
  );
  const { data: previousResponseA, isLoading: isLoadingPrevA } = useCustomCampaignAnalyticQueryV2(
    previousVariablesA,
    {
      enabled: isEnabledPrevA,
    },
  );
  const { data: previousResponseB, isLoading: isLoadingPrevB } = useCustomCampaignAnalyticQueryV2(
    previousVariablesB,
    {
      enabled: isEnabledPrevB,
    },
  );

  const currentDetailA = useMemo(
    () =>
      extractCampaignDetailData({
        response: currentResponseA,
        variantKey: IVariantType.VARIANT_A,
        interval: timeDimension,
      }),
    [currentResponseA, timeDimension],
  );

  const currentDetailB = useMemo(
    () =>
      extractCampaignDetailData({
        response: currentResponseB,
        variantKey: IVariantType.VARIANT_B,
        interval: timeDimension,
      }),
    [currentResponseB, timeDimension],
  );

  const previousDetailA = useMemo(
    () =>
      extractCampaignDetailData({
        response: previousResponseA,
        variantKey: IVariantType.VARIANT_A,
        interval: timeDimension,
      }),
    [previousResponseA, timeDimension],
  );

  const previousDetailB = useMemo(
    () =>
      extractCampaignDetailData({
        response: previousResponseB,
        variantKey: IVariantType.VARIANT_B,
        interval: timeDimension,
      }),
    [previousResponseB, timeDimension],
  );

  const campaignAnalytic = useMemo(
    () => [
      buildAnalyticEntry(campaignVariantIdA, IVariantType.VARIANT_A, currentDetailA.totalsMetric),
      buildAnalyticEntry(campaignVariantIdB, IVariantType.VARIANT_B, currentDetailB.totalsMetric),
    ],
    [
      campaignVariantIdA,
      campaignVariantIdB,
      currentDetailA.totalsMetric,
      currentDetailB.totalsMetric,
    ],
  );

  const previousCampaignAnalytic = useMemo(
    () => [
      buildAnalyticEntry(campaignVariantIdA, IVariantType.VARIANT_A, previousDetailA.totalsMetric),
      buildAnalyticEntry(campaignVariantIdB, IVariantType.VARIANT_B, previousDetailB.totalsMetric),
    ],
    [
      campaignVariantIdA,
      campaignVariantIdB,
      previousDetailA.totalsMetric,
      previousDetailB.totalsMetric,
    ],
  );

  const metricInterval = useMemo<IVariantIntervalSeries[]>(
    () => [
      { data: currentDetailA.intervalMetrics, variantKey: IVariantType.VARIANT_A },
      { data: currentDetailB.intervalMetrics, variantKey: IVariantType.VARIANT_B },
    ],
    [currentDetailA.intervalMetrics, currentDetailB.intervalMetrics],
  );

  const isLoadingMetric = isQueryPreviousData
    ? (isEnabledA && isLoadingCurrentA) ||
      (isEnabledB && isLoadingCurrentB) ||
      (isEnabledPrevA && isLoadingPrevA) ||
      (isEnabledPrevB && isLoadingPrevB)
    : (isEnabledA && isLoadingCurrentA) || (isEnabledB && isLoadingCurrentB);

  return {
    campaignAnalytic,
    previousCampaignAnalytic,
    metricInterval,
    interval: TIME_DIMENSION_TO_INTERVAL[timeDimension],
    isLoadingMetric,
    isLoadingMetricInterval: isLoadingMetric,
  };
};
